/**
 * Homepage team block — scroll scrub (sticky section + per-card translateY).
 * Each card rises at its own speed (parallax): lower cards faster, top cards slower.
 * Disabled when prefers-reduced-motion. Synced with Lenis scroll.
 */

import { getScrollY, onLenisScroll } from './lenis-scroll';

const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';
const MAX_STYLED_CARDS = 10;
const DEFAULT_END_PADDING = 48;
/** Top cards rise slower, bottom cards faster (wider spread = stronger parallax). */
const CARD_RISE_SPEED_MIN = 0.62;
const CARD_RISE_SPEED_MAX = 1.15;
const CARD_Z_INDEX_BASE = 10;

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

function getCardRiseSpeed(index, cardCount) {
	if (cardCount <= 1) {
		return 1;
	}

	const t = index / (cardCount - 1);
	return CARD_RISE_SPEED_MIN + t * (CARD_RISE_SPEED_MAX - CARD_RISE_SPEED_MIN);
}

class TeamScrollScrub {
	constructor(zone) {
		this.zone = zone;
		this.section = zone.querySelector('.home-section--team');
		this.container = zone.querySelector('.frge-teams-container');
		this.wrapper = zone.querySelector('.frge-teams-wrapper');
		this.cards = [];
		this.maxTranslate = 0;
		this.zoneTop = 0;
		this.enabled = false;
		this.ticking = false;
		this.onScrollBound = () => this.requestTick();
		this.onResizeBound = () => this.recalculate();
		this.unsubscribeLenis = null;
	}

	requestTick() {
		if (this.ticking) {
			return;
		}
		this.ticking = true;
		requestAnimationFrame(() => {
			this.ticking = false;
			this.onScroll();
		});
	}

	enable() {
		if (!this.section || !this.container || !this.wrapper) {
			return;
		}

		const teamCount = Number.parseInt(this.zone.dataset.teamCount || '0', 10);
		if (teamCount > MAX_STYLED_CARDS) {
			// eslint-disable-next-line no-console
			console.warn(
				`[home-team-scroll] ${teamCount} team members but only ${MAX_STYLED_CARDS} have nth-child positions in SCSS.`,
			);
		}

		this.cards = [...this.wrapper.querySelectorAll('.frge-team-card')];
		this.enabled = true;
		this.recalculate();
		this.unsubscribeLenis = onLenisScroll(this.onScrollBound);
		window.addEventListener('scroll', this.onScrollBound, { passive: true });
		window.addEventListener('resize', this.onResizeBound);
	}

	disable() {
		this.enabled = false;
		if (this.unsubscribeLenis) {
			this.unsubscribeLenis();
			this.unsubscribeLenis = null;
		}
		window.removeEventListener('scroll', this.onScrollBound);
		window.removeEventListener('resize', this.onResizeBound);
		this.zone.style.height = '';
		this.wrapper.style.transform = '';
		this.cards.forEach((card) => {
			card.style.transform = '';
			card.style.zIndex = '';
		});
		this.zone.classList.remove('is-scrubbing');
		this.maxTranslate = 0;
	}

	getEndPadding() {
		const raw = getComputedStyle(this.zone)
			.getPropertyValue('--team-scroll-end-padding')
			.trim();
		const parsed = Number.parseFloat(raw);
		return Number.isFinite(parsed) ? parsed : DEFAULT_END_PADDING;
	}

	measureMaxTranslate() {
		const lastCard = this.wrapper.querySelector('.frge-team-card:last-child');
		if (!lastCard) {
			return 0;
		}

		this.wrapper.style.transform = 'translate3d(0, 0, 0)';
		this.cards.forEach((card) => {
			card.style.transform = '';
		});

		const containerRect = this.container.getBoundingClientRect();
		const lastRect = lastCard.getBoundingClientRect();
		const endPadding = this.getEndPadding();

		// Remonter assez pour que toute la carte tienne dans le clip (+ marge bas)
		const toRevealBottom = lastRect.bottom - containerRect.bottom + endPadding;
		const toRevealTop = lastRect.top - containerRect.top + endPadding;

		return Math.max(0, Math.ceil(Math.max(toRevealBottom, toRevealTop)));
	}

	recalculate() {
		if (!this.enabled) {
			return;
		}

		this.maxTranslate = this.measureMaxTranslate();

		if (this.maxTranslate <= 0) {
			this.zone.style.height = '';
			this.onScroll();
			return;
		}

		this.zone.style.height = `${this.section.offsetHeight + this.maxTranslate}px`;
		this.zoneTop = this.zone.getBoundingClientRect().top + getScrollY();
		this.onScroll();
	}

	onScroll() {
		if (!this.enabled || this.maxTranslate <= 0) {
			this.wrapper.style.transform = '';
			this.cards.forEach((card) => {
				card.style.transform = '';
				card.style.zIndex = '';
			});
			this.zone.classList.remove('is-scrubbing');
			return;
		}

		const progress = clamp((getScrollY() - this.zoneTop) / this.maxTranslate, 0, 1);
		const baseOffset = progress * this.maxTranslate;
		const cardCount = this.cards.length;

		this.wrapper.style.transform = '';
		this.cards.forEach((card, index) => {
			const cardOffset = baseOffset * getCardRiseSpeed(index, cardCount);

			if (cardOffset > 0) {
				card.style.transform = `translate3d(0, ${-cardOffset}px, 0)`;
				// Lower cards (higher index) stack above while scrolling.
				card.style.zIndex = String(CARD_Z_INDEX_BASE + index);
			} else {
				card.style.transform = '';
				card.style.zIndex = '';
			}
		});
		this.zone.classList.toggle('is-scrubbing', progress > 0 && progress < 1);
	}

	observeImages() {
		if (!this.wrapper) {
			return;
		}

		this.wrapper.querySelectorAll('img').forEach((img) => {
			if (img.complete) {
				return;
			}
			img.addEventListener('load', this.onResizeBound, { once: true });
		});
	}

	observeResize() {
		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		this.resizeObserver = new ResizeObserver(() => this.recalculate());
		if (this.section) {
			this.resizeObserver.observe(this.section);
		}
		if (this.container) {
			this.resizeObserver.observe(this.container);
		}
	}

	destroy() {
		this.disable();
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	}
}

function initTeamScroll() {
	if (window.matchMedia(REDUCED_MOTION_MQ).matches) {
		return;
	}

	document.querySelectorAll('[data-team-scroll]').forEach((zone) => {
		const scrub = new TeamScrollScrub(zone);

		scrub.observeImages();
		scrub.observeResize();
		scrub.enable();
	});
}

document.addEventListener('DOMContentLoaded', initTeamScroll);
